<script>
  import { fly } from 'svelte/transition';
  import { identity } from 'ramda';
  import { onMount, tick } from 'svelte';
  import { derived } from 'svelte/store';
  import Motion, { useMotion } from '../Motion';
  import { onScreen as easing } from '../Motion/easing';
  import { EXTRA_FAST } from '../Motion/duration';

  const motion = useMotion();

  export let dropdown;

  let container;

  onMount(() => {
    container.append(dropdown.element);
    return () => {
      dropdown.element.remove();
      dropdown.dispatch('remove');
    };
  });

  function keyHandler(event) {
    if (event.key === 'Escape' || event.key === 'Tab') {
      dropdown.dismiss();
    }
  }

  const anchorPosition = derived([
    dropdown.anchor, dropdown.gravity,
  ], async ([$anchor, $gravity], set) => { // eslint-disable-line consistent-return
    if (!$anchor || !$gravity) {
      return '';
    }

    const relativeToDocument = ({
      top, bottom, left, right,
    }) => ({
      top: document.body.clientHeight - (top + window.scrollY),
      bottom: bottom + window.scrollY,
      left: left + window.scrollX,
      right: document.body.clientWidth - (right + window.scrollX),
    });

    const bounds = $anchor.getBoundingClientRect();
    const position = relativeToDocument(bounds);

    await tick();
    let { width: dropdownWidth, height: dropdownHeight } = dropdown.element.getBoundingClientRect();
    dropdownWidth = Math.max(dropdownWidth, bounds.width);
    if (bounds.bottom + dropdownHeight > window.innerHeight) {
      if (bounds.bottom <= window.innerHeight / 2) {
        dropdownHeight = window.innerHeight - bounds.bottom - 9;
      } else {
        position.bottom -= dropdownHeight + bounds.height + 9;
      }
    }
    if (bounds.left - dropdownWidth < 0) {
      position.right -= dropdownWidth - bounds.width;
    }
    if (bounds.left + dropdownWidth > window.innerWidth) {
      position.left -= dropdownWidth - bounds.width;
    }

    const useRight = $gravity.includes('left');
    const useLeft = !useRight && $gravity.includes('right');
    const useBottom = $gravity.includes('top');
    const useTop = !useBottom && $gravity.includes('bottom');
  
    const top = useTop ? `${position.bottom}px` : 'unset';
    const right = useRight ? `${position.right}px` : 'unset';
    const bottom = useBottom ? `${position.top}px` : 'unset';
    const left = useLeft ? `${position.left}px` : 'unset';

    set(`
      position: absolute;
      top: ${top};
      bottom: ${bottom};
      right: ${right};
      left: ${left};
      width: ${dropdownWidth}px;
      height: ${dropdownHeight}px;
    `);
  });

  const dropdownClassName = derived(dropdown.className, identity);
  const backdropClickThrough = derived(dropdown.clickThrough, identity);
</script>

<svelte:window on:keydown={keyHandler} />
<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
  class='dropdown-backdrop'
  class:backdrop-see-through={$backdropClickThrough}
  data-cy='dismiss-dropdown'
  on:click={() => dropdown.dismiss()}>
  <div
    style={$anchorPosition}
    bind:this={container}
    on:click|stopPropagation
    transition:fly={{
      opacity: 0,
      easing,
      y: -4,
      duration: motion === Motion.NONE ? 0 : EXTRA_FAST,
    }}
    class='dropdown-container {$dropdownClassName}'
    class:container-clickable="{$backdropClickThrough}" />
</div>

<style>
  .dropdown-backdrop {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    z-index: var(--Dropdown--z-index);
  }

  .backdrop-see-through {
    pointer-events: none;
  }

  .container-clickable {
    pointer-events: auto;
  }

  .dropdown-container {
    --Dropdown--background-color: var(--color--default--background);
    --Dropdown--shadow: 0 1px 4px var(--color--shadow);

    position: absolute;
    margin: var(--spacing--xxs) 0;
    border-radius: var(--radius--m);
    background-color: var(--Dropdown--background-color);
    box-shadow: var(--Dropdown--shadow);
    overflow-x: hidden;
    overflow-y: auto;
  }

  .content-tones-container {
    padding: var(--spacing--xxs) 0px;
    margin: 0px;
  }
</style>
